import React, { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSpring, animated } from 'react-spring';
import { Icon } from '@iconify/react';
import informationIcon from '@iconify-icons/ion/information';
import { getBanners } from './bannerData';
import { fixedEncodeURIComponent, getLetterGradeFromNumberGrade, isUserHasKey } from '../../GlobalFunctions';
import { showRightArrowWhiteIcon } from '../../core/core';
import Button from '../Button';
import styles from './LibraryBanner.module.scss';
import MuxPlayer from '../MuxPlayer';
import { ClassroomType, UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import { getUsersEnableContentTags } from '../../GlobalFunctions';
import { getUserProfile } from '../../BackendInterface';

interface Props {
  bannerFilter: string;
  level?: string;
  toggleBanner: (arg0: boolean) => void;
  isShowBanner: boolean;
  classroom: ClassroomType;
  handleClickLibraryFilter?: (arg0: string) => void;
  userProfile?: UserProfileType | null;
}

const LibraryBanner = ({
  bannerFilter,
  level,
  toggleBanner,
  isShowBanner,
  classroom,
  handleClickLibraryFilter,
  userProfile,
}: Props): JSX.Element => {
  const [isShowPlayer, setIsShowPlayer] = useState(false);

  const history = useHistory();

  //Get banners with license-level customization
  const availableBanners = getBanners(userProfile, classroom?.grade);

  //Filtering out by month
  const currentMonth = new Date().getMonth();
  const monthFiltered = availableBanners.filter(
    (b) => !b.months || b.months.length === 0 || b.months.includes(currentMonth),
  ); //If month is blank, its for all months

  //Filtering out mena/not-mena
  const enabledContentTags = getUsersEnableContentTags() ?? [];
  const isMena = enabledContentTags.includes('mena');

  const regionFiltered = monthFiltered.filter((b) => {
    const tags = b.applicationTags ?? [];
    if (!tags.length) return true; //if no tag, show to all region
    if (tags.includes('mena')) return isMena;
    if (tags.includes('not-mena')) return !isMena;
    return true;
  });

  //console.log('userProfileee:', userProfile?.role);
  const accountRole = userProfile?.role ?? 'Teacher';

  let bannersToShow: typeof availableBanners = [];

  if (
    accountRole === 'District Administrator' ||
    accountRole === 'School Administrator' ||
    accountRole === 'Principal'
  ) {
    bannersToShow = regionFiltered.filter((b) => b.keyRequired?.includes('KEY_MANAGE_STUDENTS'));
  } else if (
    accountRole === 'Teacher' ||
    accountRole === 'Librarian' ||
    accountRole === 'Counselor' ||
    accountRole === 'Social Emotional Learning Lead' ||
    accountRole === 'Homeschool Teacher' ||
    accountRole === 'Parent' ||
    accountRole === 'Other'
  ) {
    bannersToShow = regionFiltered.filter((b) => b.keyRequired?.includes('KEY_ALL_LESSON_PLANS'));
  } else {
    bannersToShow = regionFiltered.filter((b) => b.keyRequired?.includes('KEY_BASIC'));
  }

  const getFilteredBanner = () => {
    return bannersToShow.find((banner) => {
      const matchesFilterTag = banner.filterTag === bannerFilter;

      //level-safe matching
      const matchesLevel =
        banner.level == null || //blank = show for all
        (Array.isArray(banner.level)
          ? banner.level.length === 0 || // blank = show for all
            (level ? banner.level.includes(level) : true)
          : banner.level === level);

      return matchesFilterTag && matchesLevel;
    });
  };

  const isElementary = parseInt(classroom.grade.toString()) < 6;

  const filteredBanner = getFilteredBanner();

  // For Takween users, prioritize their custom default banner
  const isTakweenUser = userProfile?.licenseLevel === 'TAKWEEN';
  let defaultBanner;

  if (isTakweenUser) {
    // Find the Takween default banner (filterTag: '')
    defaultBanner = bannersToShow.find((banner) => banner.filterTag === '') || availableBanners[0];
  } else {
    defaultBanner = isElementary ? availableBanners[0] : availableBanners[1];
  }

  const displayBanner = filteredBanner ?? defaultBanner;

  // destructure other fields, but NOT imgURL
  const { title, subtitle, content, imgAlt, buttons, logoURLs } = displayBanner;
  const imgURL = isMena && displayBanner.menaURL ? displayBanner.menaURL : displayBanner.imgURL;

  const handleShowHide = (e: React.MouseEvent<HTMLButtonElement>) => {
    toggleBanner(!isShowBanner);
    e.currentTarget.blur();
  };

  const letterGrade = getLetterGradeFromNumberGrade(classroom.grade);
  const peekavilleIntro = `/${fixedEncodeURIComponent(classroom.className)}/lessonPlan/Getting-Started-${letterGrade}`;

  const buttonSpringProps = useSpring({
    top: isShowBanner ? 10 : -45,
  });
  const bannerSpringProps = useSpring({
    height: isShowBanner ? 'fit-content' : 0,
    padding: isShowBanner ? '2em' : '0',
    marginBottom: isShowBanner ? '2em' : '0',
    opacity: isShowBanner ? 1 : 0,
  });

  const handleHide = () => setIsShowPlayer(false);

  return (
    <div className={styles.container}>
      <animated.div className={styles.bannerContainer} style={bannerSpringProps}>
        <div className={styles.leftColumn}>
          <h3>{title}</h3>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          <p style={{ whiteSpace: 'pre-line' }}>{content}</p>
          <div className={styles.buttonRow}>
            {buttons?.map((button, i) => {
              return (
                <Fragment key={`banner button ${i}`}>
                  {button.type === 'unitLink' && isElementary && (
                    <Button
                      key={`${displayBanner.title}button${i}`}
                      small
                      onClick={() => history.push(peekavilleIntro)}
                    >
                      {button.text}&nbsp;&nbsp;
                      {showRightArrowWhiteIcon()}
                    </Button>
                  )}
                  {button.type === 'videoLink' && (
                    <React.Fragment key={`${displayBanner.title}button${i}`}>
                      <Button small onClick={() => setIsShowPlayer(true)}>
                        {button.text}&nbsp;&nbsp;
                        {showRightArrowWhiteIcon()}
                      </Button>
                      {isShowPlayer && button.assetId && (
                        <MuxPlayer
                          videoId={button?.assetId}
                          isShow={isShowPlayer}
                          onHide={handleHide}
                          title={button.text}
                        />
                      )}
                    </React.Fragment>
                  )}
                  {button.type === 'internalLinkPush' && button.linkLocation && (
                    <Button
                      key={`${displayBanner.title}button${i}`}
                      small
                      onClick={() => history.push(button.linkLocation!)}
                    >
                      {button.text}&nbsp;&nbsp;
                      {showRightArrowWhiteIcon()}
                    </Button>
                  )}
                  {button.type === 'internalLinkWindow' && (
                    <Button
                      key={`${displayBanner.title}button${i}`}
                      small
                      onClick={() => window.open(button.linkLocation, '_blank')}
                    >
                      {button.text}&nbsp;&nbsp;
                      {showRightArrowWhiteIcon()}
                    </Button>
                  )}
                  {button.type === 'libraryFilterLink' && (
                    <Button
                      key={`${displayBanner.title}button${i}`}
                      small
                      onClick={() =>
                        button.filterValue && handleClickLibraryFilter && handleClickLibraryFilter(button.filterValue)
                      }
                    >
                      {button.text}&nbsp;&nbsp;
                      {showRightArrowWhiteIcon()}
                    </Button>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
        {logoURLs ? (
          <div className={styles.logoContainer}>
            {logoURLs.map((logoUrl, index) => (
              <img key={`logo-${index}`} src={logoUrl} alt={`Logo ${index + 1}`} className={styles.logo} />
            ))}
          </div>
        ) : (
          imgURL && <img src={imgURL} alt={imgAlt} />
        )}
      </animated.div>
      <div className={styles.closeButton}>
        <animated.button onClick={handleShowHide} style={buttonSpringProps}>
          {isShowBanner ? <Icon icon='charm:cross' /> : <Icon icon={informationIcon} />}
        </animated.button>
      </div>
    </div>
  );
};

export default LibraryBanner;
